import React, { useState, useEffect, useRef } from 'react';

interface Props {
  toolbarId: string;
  fontSize: number;
  onFontSizeChange: (size: number) => void;
}

const clamp = (v: number) => Math.min(100, Math.max(8, v));

const SIZE_PRESETS: Array<{ label: string; px: number }> = [
  { label: 'Small', px: 12 },
  { label: 'Default', px: 14 },
  { label: 'Medium', px: 16 },
  { label: 'Large', px: 20 },
  { label: 'Extra Large', px: 26 },
];

const presetButtonStyle: React.CSSProperties = {
  height: 24,
  minWidth: 96,
  padding: '0 8px',
  fontSize: 12,
  border: '1px solid #ccc',
  borderRadius: 3,
  background: '#fff',
  color: '#444',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
};

const presetMenuStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  zIndex: 10,
  margin: 0,
  padding: '4px 0',
  listStyle: 'none',
  minWidth: 120,
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 3,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
};

const presetMenuItemStyle = (selected: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  fontSize: 12,
  cursor: 'pointer',
  background: selected ? '#f0f0f0' : 'transparent',
  color: '#444',
});

const btnStyle: React.CSSProperties = {
  width: 20,
  height: 22,
  border: '1px solid #ccc',
  borderRadius: 3,
  cursor: 'pointer',
  background: '#fafafa',
  fontSize: 14,
  lineHeight: 1,
  color: '#444',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  flexShrink: 0,
};

const inputStyle: React.CSSProperties = {
  width: 40,
  height: 22,
  textAlign: 'center',
  border: '1px solid #ccc',
  borderRadius: 3,
  fontSize: 12,
  padding: '0 2px',
  outline: 'none',
  flexShrink: 0,
  // Hide native spinner arrows
  MozAppearance: 'textfield' as any,
};

const QuillToolbarWithFontSize: React.FC<Props> = ({ toolbarId, fontSize, onFontSizeChange }) => {
  const [inputValue, setInputValue] = useState(String(fontSize));
  const [presetOpen, setPresetOpen] = useState(false);
  const presetRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setInputValue(String(fontSize));
  }, [fontSize]);

  useEffect(() => {
    if (!presetOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (presetRef.current && !presetRef.current.contains(e.target as Node)) {
        setPresetOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [presetOpen]);

  const commit = (raw: string) => {
    const v = parseInt(raw, 10);
    if (!isNaN(v)) onFontSizeChange(clamp(v));
    else setInputValue(String(fontSize));
  };

  const currentPreset = SIZE_PRESETS.find((p) => p.px === fontSize);

  return (
    <div id={toolbarId}>
      <span className="ql-formats" ref={presetRef} style={{ position: 'relative' }}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={presetOpen}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setPresetOpen((o) => !o)}
          style={presetButtonStyle}
        >
          <span>{currentPreset?.label ?? `${fontSize}px`}</span>
          <span aria-hidden style={{ fontSize: 9, lineHeight: 1 }}>{'▾'}</span>
        </button>
        {presetOpen && (
          <ul role="listbox" style={presetMenuStyle}>
            {SIZE_PRESETS.map((p) => (
              <li
                key={p.px}
                role="option"
                aria-selected={p.px === fontSize}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onFontSizeChange(clamp(p.px));
                  setPresetOpen(false);
                }}
                style={presetMenuItemStyle(p.px === fontSize)}
              >
                {p.label}
              </li>
            ))}
          </ul>
        )}
      </span>
      <span className="ql-formats">
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
        <button className="ql-strike" />
      </span>
      <span className="ql-formats">
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
      </span>
      <span className="ql-formats">
        <button className="ql-link" />
      </span>
      <span className="ql-formats">
        <select className="ql-color" />
        <select className="ql-background" />
      </span>
      <span className="ql-formats">
        <button className="ql-clean" />
        {/* Custom "clear colour" button — icon/handler wired via quillColorReset */}
        <button type="button" className="ql-resetColor" data-tooltip="Clear color" />
      </span>
      <span className="ql-formats" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'middle' }}>
        <button
          type="button"
          style={btnStyle}
          onMouseDown={(e) => { e.preventDefault(); onFontSizeChange(clamp(fontSize - 1)); }}
        >
          −
        </button>
        <input
          type="number"
          value={inputValue}
          min={8}
          max={100}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault(); // block native spinner behaviour
            }
          }}
          onBlur={() => commit(inputValue)}
          style={inputStyle}
        />
        <button
          type="button"
          style={btnStyle}
          onMouseDown={(e) => { e.preventDefault(); onFontSizeChange(clamp(fontSize + 1)); }}
        >
          +
        </button>
      </span>
      {/* Hide WebKit spinner arrows */}
      <style>{`
        #${toolbarId} input[type=number]::-webkit-inner-spin-button,
        #${toolbarId} input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>
    </div>
  );
};

export default QuillToolbarWithFontSize;
